import React, { useEffect, useState } from 'react';
import Typography from '@material-ui/core/Typography';
import { Button, FormControlLabel, Grid, Switch, TextField } from '@material-ui/core';

import adminApi from '../../Api/adminApi';
import store from '../../redux/store';
import { showError, showSuccess } from '../../redux/actions';
import { COLORS2 } from '../../constants';
import { errorMessage } from './adminFormat';

// Same rules as parsePlanUpdate on the server: integers >= -1, -1 meaning unlimited.
function parseLimit(value) {
    const n = Number(value);
    return Number.isInteger(n) && n >= -1 ? n : null;
}

/**
 * Edits one plan definition (limits and feature flags). Prices are read-only
 * here: they come from Stripe at startup. The change applies to every user on
 * the plan as soon as it is saved, so it is confirmed with a before/after summary.
 */
const AddEditSubscription = ({ subscription, pricing, handleClose, onSaved }) => {
    const [stripeId, setStripeId] = useState('');
    const [groupCount, setGroupCount] = useState('');
    const [collectionCount, setCollectionCount] = useState('');
    const [multipleIndividual, setMultipleIndividual] = useState(false);
    const [pdf, setPdf] = useState(false);
    const [audioVideo, setAudioVideo] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        setStripeId(subscription.subscriptionStripeId || '');
        setGroupCount(String(subscription.groupCount));
        setCollectionCount(String(subscription.collectionCount));
        setMultipleIndividual(!!subscription.multipleIndividualBoards);
        setPdf(!!subscription.pdfDownload);
        setAudioVideo(!!subscription.audioVideo);
        setError(null);
    }, [subscription]);

    const next = {
        subscriptionStripeId: stripeId.trim(),
        groupCount: parseLimit(groupCount),
        collectionCount: parseLimit(collectionCount),
        multipleIndividualBoards: multipleIndividual,
        pdfDownload: pdf,
        audioVideo: audioVideo,
    };
    const valid = next.subscriptionStripeId && next.groupCount !== null && next.collectionCount !== null;

    const changes = Object.keys(next).filter((key) => {
        const before = key === 'subscriptionStripeId' ? subscription[key] || '' : subscription[key];
        return String(before) !== String(next[key]);
    });

    const updateSubscription = () => {
        if (!valid || changes.length === 0) {
            return;
        }
        const summary = changes.map((key) => `${key}: ${String(subscription[key])} → ${String(next[key])}`).join('\n');
        if (!window.confirm(`Save plan ${subscription.subscriptionName}?\n\n${summary}\n\nThis applies to every user on the plan immediately.`)) {
            return;
        }
        setBusy(true);
        setError(null);
        adminApi
            .updateSubscriptionType(subscription.id, next)
            .then((res) => {
                store.dispatch(showSuccess('ADMIN_PLAN_UPDATED'));
                if (onSaved) {
                    onSaved(res.data);
                }
                handleClose();
            })
            .catch((err) => {
                console.error(err);
                setError(errorMessage(err));
                store.dispatch(showError('GENERIC_ERROR'));
            })
            .finally(() => setBusy(false));
    };

    const prices = pricing && pricing[subscription.subscriptionName];

    return (
        <Grid container direction="column" spacing={2}>
            <Grid item>
                <Typography variant="subtitle2" color="textSecondary">
                    Payment (from Stripe, read-only)
                </Typography>
                {prices && prices.length ? (
                    prices.map((el, index) => (
                        <Typography key={'price-' + subscription.subscriptionName + '-' + index}>
                            {el.unit_amount / 100} {el.currency} / {el.interval}
                        </Typography>
                    ))
                ) : (
                    <Typography>Free</Typography>
                )}
            </Grid>
            <Grid item>
                <TextField
                    fullWidth
                    label="Stripe product id"
                    variant="outlined"
                    size="small"
                    value={stripeId}
                    error={!stripeId.trim()}
                    onChange={(e) => setStripeId(e.target.value)}
                />
            </Grid>
            <Grid item>
                <TextField
                    fullWidth
                    label="Max groups (-1 = unlimited)"
                    variant="outlined"
                    size="small"
                    type="number"
                    value={groupCount}
                    error={parseLimit(groupCount) === null}
                    onChange={(e) => setGroupCount(e.target.value)}
                />
            </Grid>
            <Grid item>
                <TextField
                    fullWidth
                    label="Max library boards (-1 = unlimited)"
                    variant="outlined"
                    size="small"
                    type="number"
                    value={collectionCount}
                    error={parseLimit(collectionCount) === null}
                    onChange={(e) => setCollectionCount(e.target.value)}
                />
            </Grid>
            <Grid item>
                <FormControlLabel
                    control={
                        <Switch
                            color="primary"
                            checked={multipleIndividual}
                            onChange={(e) => setMultipleIndividual(e.target.checked)}
                        />
                    }
                    label="Multiple individual boards"
                />
            </Grid>
            <Grid item>
                <FormControlLabel
                    control={<Switch color="primary" checked={pdf} onChange={(e) => setPdf(e.target.checked)} />}
                    label="PDF download"
                />
            </Grid>
            <Grid item>
                <FormControlLabel
                    control={
                        <Switch color="primary" checked={audioVideo} onChange={(e) => setAudioVideo(e.target.checked)} />
                    }
                    label="Audio / video"
                />
            </Grid>
            {error ? (
                <Grid item>
                    <Typography style={{ color: COLORS2.virtualRed, fontSize: '0.88rem' }} role="alert">
                        {error}
                    </Typography>
                </Grid>
            ) : null}
            <Grid item style={{ display: 'flex', gap: '10px' }}>
                <Button
                    color="primary"
                    variant="contained"
                    disabled={busy || !valid || changes.length === 0}
                    onClick={updateSubscription}
                >
                    {changes.length === 0 ? 'No changes' : `Save ${changes.length} change(s)`}
                </Button>
                <Button variant="outlined" disabled={busy} onClick={handleClose}>
                    Cancel
                </Button>
            </Grid>
        </Grid>
    );
};

export default AddEditSubscription;
